<template>
  <b-modal
    id="advert-generation-modal"
    ref="advert-generation-modal"
    size="lg"
    no-close-on-backdrop
    modal-class="advert-generation-modal"
    :title="$t('message.settings.ai.advertGeneration.generateTitle')"
    @hide="onHide">
    <div
      v-if="loading"
      class="row">
      <div class="col text-center py-5">
        <coozzy-spinner class="m-3" />
      </div>
    </div>
    <template v-else-if="hasResult">
      <div class="row mb-3">
        <div class="col-12">
          <label class="font-weight-bold">
            {{ $t('message.onBoarding.buildings.objects.title') }}
          </label>
          <p class="mb-0">
            {{ generatedTitle }}
          </p>
        </div>
      </div>
      <div class="row mb-4">
        <div class="col-12">
          <div class="font-weight-bold">
            {{ $t('message.onBoarding.buildings.objects.description') }}
          </div>
          <div
            class="mb-0 white-space-pre-line"
            v-text="generatedDescription" />
        </div>
      </div>
      <div class="row mb-2">
        <div class="col-12">
          <coozzy-form-input
            v-model="feedbackComment"
            :name="$tc('message.settings.ai.advertGeneration.feedback')"
            :placeholder="$tc('message.settings.ai.advertGeneration.feedbackPlaceholder')" />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="w-100">
        <coozzy-button
          size="normal"
          class="float-left"
          @click="hide">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="normal"
          design="prop-green"
          class="float-right"
          :disabled="!hasResult || loading"
          @click="apply">
          {{ $t('message.generic.form.accept') }}
        </coozzy-button>
        <coozzy-button
          v-if="hasResult"
          size="normal"
          design="red"
          class="float-right mr-2"
          :disabled="loading || regenerating || !canRegenerate"
          @click="regenerateWithFeedback">
          {{ $t('message.settings.ai.advertGeneration.regenerateWithFeedback') }}
        </coozzy-button>
      </div>
    </template>
  </b-modal>
</template>

<script lang="ts">
import Vue from 'vue'
import axios from 'axios'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import AdvertGenerationApi from '@/misc/apis/AdvertGenerationApi'
import { AiadvertGenerationFeedbackRating, AiadvertGenerationGenerateResponse } from '@/types/api'

export default Vue.extend({
  name: 'AdvertGenerationModal',
  components: {
    CoozzyFormInput,
    CoozzyButton,
    CoozzySpinner
  },
  props: {
    objectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      regenerating: false,
      generatedTitle: '',
      generatedDescription: '',
      generationId: '',
      feedbackComment: '',
      abortController: null as AbortController | null
    }
  },
  computed: {
    hasResult(): boolean {
      return !!this.generationId
    },
    canRegenerate(): boolean {
      return (this.feedbackComment || '').trim().length >= 3
    }
  },
  methods: {
    show() {
      this.resetState()
      ;(this.$refs['advert-generation-modal'] as any).show()
      this.generate()
    },
    hide() {
      ;(this.$refs['advert-generation-modal'] as any).hide()
    },
    resetState() {
      this.cancelPendingRequest()
      this.loading = false
      this.regenerating = false
      this.generatedTitle = ''
      this.generatedDescription = ''
      this.generationId = ''
      this.feedbackComment = ''
    },
    cancelPendingRequest() {
      if (this.abortController && !this.abortController.signal.aborted) {
        this.abortController.abort()
      }
      this.abortController = null
    },
    isCanceled(error: any): boolean {
      return axios.isCancel(error) ||
        error?.code === 'ERR_CANCELED' ||
        error?.name === 'CanceledError' ||
        error?.name === 'AbortError'
    },
    onHide() {
      this.resetState()
    },
    applyGenerationResult(data: AiadvertGenerationGenerateResponse) {
      this.generatedTitle = data.title || ''
      this.generatedDescription = data.description || ''
      this.generationId = data.generationId || ''
      this.feedbackComment = ''
    },
    async generate() {
      this.cancelPendingRequest()
      this.abortController = new AbortController()
      this.loading = true
      try {
        const data = await AdvertGenerationApi.generate(String(this.objectId), {
          signal: this.abortController.signal
        })
        this.applyGenerationResult(data)
      } catch (e) {
        if (this.isCanceled(e)) {
          return
        }
        console.error(e)
        Vue.toasted.show(this.$t('message.settings.ai.advertGeneration.generateFailed'), { type: 'error' })
        this.hide()
      } finally {
        this.loading = false
        this.abortController = null
      }
    },
    async regenerateWithFeedback() {
      if (!this.canRegenerate) {
        return
      }
      const comment = this.feedbackComment.trim()
      const generationId = this.generationId
      this.cancelPendingRequest()
      this.abortController = new AbortController()
      this.loading = true
      this.regenerating = true
      try {
        try {
          await AdvertGenerationApi.submitFeedback(
            generationId,
            AiadvertGenerationFeedbackRating.FEEDBACK_RATING_NEGATIVE,
            comment
          )
        } catch (feedbackError) {
          console.error(feedbackError)
          Vue.toasted.show(this.$t('message.settings.ai.advertGeneration.feedbackFailed'), { type: 'error' })
        }
        const data = await AdvertGenerationApi.regenerate(
          generationId,
          comment,
          { signal: this.abortController.signal }
        )
        this.applyGenerationResult(data)
      } catch (e) {
        if (this.isCanceled(e)) {
          return
        }
        console.error(e)
        Vue.toasted.show(this.$t('message.settings.ai.advertGeneration.regenerateFailed'), { type: 'error' })
      } finally {
        this.loading = false
        this.regenerating = false
        this.abortController = null
      }
    },
    apply() {
      if (!this.hasResult) {
        return
      }
      this.$emit('apply', {
        title: this.generatedTitle,
        description: this.generatedDescription,
        generationId: this.generationId
      })
      this.hide()
    }
  }
})
</script>

<style lang="scss" scoped>
.white-space-pre-line {
  white-space: pre-line;
}
</style>

<style lang="scss">
.advert-generation-modal .modal-footer {
  border-top: 0;
}
</style>
